import { customElement } from "lit/decorators.js";
import { PropertyValues } from "lit";
import { RenderPass } from "three/examples/jsm/postprocessing/RenderPass.js";
import { TAARenderPass } from "three/examples/jsm/postprocessing/TAARenderPass.js";
import { SugarElement } from "../configurator/SugarCanvas";
import { Engine, Entity } from "../engine/Engine";
import { CameraTracker } from "../engine/camera/CameraTracker";

/**
 * three.js's `TAARenderPass` exposes `accumulateIndex` on the JS class but its
 * upstream `.d.ts` omits it. Cast through this type to manipulate it without
 * spreading `as any` through the file.
 */
type TAAPassWithInternals = TAARenderPass & {
  accumulateIndex: number;
  _sampleRenderTarget: { dispose?: () => void } | null;
  _holdRenderTarget: { dispose?: () => void } | null;
};

function asInternal(p: TAARenderPass): TAAPassWithInternals {
  return p as TAAPassWithInternals;
}

/**
 * Idle-only TAA. Wraps three.js `TAARenderPass` and toggles its `accumulate`
 * flag: birikim yalnızca **tüm** eşzamanlı tutmalar bittiğinde açılır
 * (pointer/orbit, kamera hareketi, model sürükleme, transform gizmo).
 * `transform-changed` her karede geldiği için dinlenmez.
 *
 * Because the pass renders the scene itself (it inherits from
 * `SSAARenderPass`), it must *replace* the composer's initial `RenderPass`
 * rather than be chained as a post-process. On removal the original
 * `RenderPass` is restored so the pipeline returns to its prior state.
 *
 * Attributes:
 *   - `sample-level` (0..5, default 5): max accumulated samples = 2^level.
 *     Levels above 5 are clamped because three.js only ships 32 jitter
 *     vectors.
 *   - `unbiased` (boolean, default false): forwarded to `TAARenderPass`.
 */
/** TAA birikimini durduran eşzamanlı etkinlikler (pointer, kamera, sürükleme, gizmo). */
const TAA_HOLD_INTERACTION = "interaction";
const TAA_HOLD_CAMERA_MOTION = "camera-motion";
const TAA_HOLD_DRAGGING = "dragging";
const TAA_HOLD_TRANSFORM_CONTROLS = "transform-controls";

class SugarTAAPostProcessEntity extends Entity {
  private _taaPass?: TAARenderPass;
  private _originalRenderPass?: RenderPass;
  private _sampleLevel = 5;
  private _unbiased = false;
  private _tracker?: CameraTracker;

  /** Her anahtar için iç içe begin/end sayacı; toplam aktif tutma 0 iken birikim açılır. */
  private readonly _accumulationHoldCounts = new Map<string, number>();
  private _accumulationHoldDepth = 0;

  private _applyPauseAccumulation(): void {
    if (!this._taaPass) return;
    if (!this._taaPass.accumulate) return;
    this._taaPass.accumulate = false;
    this._taaPass.sampleLevel = 0;
  }

  private _applyResumeAccumulation(): void {
    if (!this._taaPass) return;
    this._taaPass.accumulate = true;
    this._taaPass.sampleLevel = this._sampleLevel;
    asInternal(this._taaPass).accumulateIndex = -1;
    this.engine?.dispatchCustomEvent("request-update");
  }

  private _beginAccumulationHold(key: string): void {
    const prev = this._accumulationHoldCounts.get(key) ?? 0;
    if (prev === 0) {
      this._accumulationHoldDepth++;
      if (this._accumulationHoldDepth === 1) {
        this._applyPauseAccumulation();
      }
    }
    this._accumulationHoldCounts.set(key, prev + 1);
  }

  private _endAccumulationHold(key: string): void {
    const prev = this._accumulationHoldCounts.get(key) ?? 0;
    if (prev === 0) return;
    if (prev === 1) {
      this._accumulationHoldCounts.delete(key);
      this._accumulationHoldDepth--;
      if (this._accumulationHoldDepth === 0) {
        this._applyResumeAccumulation();
      }
    } else {
      this._accumulationHoldCounts.set(key, prev - 1);
    }
  }

  private _interactionStartHandler = () =>
    this._beginAccumulationHold(TAA_HOLD_INTERACTION);
  private _interactionEndHandler = () =>
    this._endAccumulationHold(TAA_HOLD_INTERACTION);
  private _transformStartHandler = () =>
    this._beginAccumulationHold(TAA_HOLD_TRANSFORM_CONTROLS);
  private _transformEndHandler = () =>
    this._endAccumulationHold(TAA_HOLD_TRANSFORM_CONTROLS);
  private _draggingStartHandler = () =>
    this._beginAccumulationHold(TAA_HOLD_DRAGGING);
  private _draggingEndHandler = () =>
    this._endAccumulationHold(TAA_HOLD_DRAGGING);

  private _onUpdateCamera = () => {
    const eng = this.engine;
    if (!this._taaPass || !eng?.activeCamera) return;
    this._taaPass.camera = eng.activeCamera.instance;
    this._taaPass.scene = eng.getSceneForWebGLRender();
    asInternal(this._taaPass).accumulateIndex = -1;
  };

  private _onWebglRenderSceneChanged = () => {
    const eng = this.engine;
    if (!this._taaPass || !eng) return;
    this._taaPass.scene = eng.getSceneForWebGLRender();
    asInternal(this._taaPass).accumulateIndex = -1;
  };

  private _onSceneChanged = () => {
    if (!this._taaPass) return;
    asInternal(this._taaPass).accumulateIndex = -1;
    this.engine?.dispatchCustomEvent("request-update");
  };

  private _onEngineResize = () => {
    if (!this._taaPass) return;
    // Internal sample/hold RTs are sized to the initial readBuffer and never
    // resized by TAARenderPass itself. Dispose and null them so they are
    // lazily reallocated at the new size on the next render.
    const p = asInternal(this._taaPass);
    p._sampleRenderTarget?.dispose?.();
    p._sampleRenderTarget = null;
    p._holdRenderTarget?.dispose?.();
    p._holdRenderTarget = null;
    p.accumulateIndex = -1;
  };

  public setSampleLevel(level: number): void {
    const clamped = level;//Math.max(0, Math.min(1, Math.floor(level)));
    this._sampleLevel = clamped;
    if (this._taaPass && this._taaPass.accumulate) {
      this._taaPass.sampleLevel = clamped;
      asInternal(this._taaPass).accumulateIndex = -1;
      this.engine?.dispatchCustomEvent("request-update");
    }
  }

  public setUnbiased(value: boolean): void {
    this._unbiased = value;
    if (this._taaPass) {
      this._taaPass.unbiased = value;
      asInternal(this._taaPass).accumulateIndex = -1;
    }
  }

  onAdd(engine: Engine): void {
    if (!engine.activeCamera) return;

    engine.mutatePassComposer((composer) => {
      const existing = composer.passes.find(
        (p) => p instanceof RenderPass,
      ) as RenderPass | undefined;
      if (existing) {
        this._originalRenderPass = existing;
        composer.removePass(existing);
      }

      const taa = new TAARenderPass(
        engine.getSceneForWebGLRender(),
        engine.activeCamera!.instance,
      );
      taa.unbiased = this._unbiased;
      taa.sampleLevel = this._sampleLevel;
      taa.accumulate = true;
      asInternal(taa).accumulateIndex = -1;
      composer.insertPass(taa, 0);
      this._taaPass = taa;

    });

    engine.addEventListener("interaction-start", this._interactionStartHandler);
    engine.addEventListener("interaction-end", this._interactionEndHandler);
    engine.addEventListener("transform-start", this._transformStartHandler);
    engine.addEventListener("transform-end", this._transformEndHandler);
    engine.addEventListener("dragging-start", this._draggingStartHandler);
    engine.addEventListener("dragging-end", this._draggingEndHandler);
    engine.addEventListener("update-camera", this._onUpdateCamera);
    engine.addEventListener(
      "webgl-render-scene-changed",
      this._onWebglRenderSceneChanged,
    );
    engine.addEventListener("engine-resize", this._onEngineResize);
    engine.addEventListener("scene-mutated", this._onSceneChanged);

    // High-level camera motion detection. Tracker filters out the per-frame
    // `camera-change` noise from SmoothOrbitController and fires clean
    // start/end transitions.
    this._tracker = new CameraTracker({
      engine,
      label: "TAA",
      onMotionStart: () => this._beginAccumulationHold(TAA_HOLD_CAMERA_MOTION),
      onMotionEnd: () => this._endAccumulationHold(TAA_HOLD_CAMERA_MOTION),
    });

    engine.dispatchCustomEvent("request-update");
  }

  onRemove(engine: Engine): void {
    this._tracker?.dispose();
    this._tracker = undefined;
    this._accumulationHoldCounts.clear();
    this._accumulationHoldDepth = 0;

    engine.mutatePassComposerIfExists((composer) => {
      if (this._taaPass) composer.removePass(this._taaPass);
      if (this._originalRenderPass) {
        composer.insertPass(this._originalRenderPass, 0);
      }
    });
    engine.removeEventListener(
      "interaction-start",
      this._interactionStartHandler,
    );
    engine.removeEventListener("interaction-end", this._interactionEndHandler);
    engine.removeEventListener("transform-start", this._transformStartHandler);
    engine.removeEventListener("transform-end", this._transformEndHandler);
    engine.removeEventListener("dragging-start", this._draggingStartHandler);
    engine.removeEventListener("dragging-end", this._draggingEndHandler);
    engine.removeEventListener("update-camera", this._onUpdateCamera);
    engine.removeEventListener(
      "webgl-render-scene-changed",
      this._onWebglRenderSceneChanged,
    );
    engine.removeEventListener("engine-resize", this._onEngineResize);
    engine.removeEventListener("scene-mutated", this._onSceneChanged);

    this._taaPass?.dispose();
    this._taaPass = undefined;
    this._originalRenderPass = undefined;
  }
}

@customElement("sugar-taa")
export class SugarTAAPostProcessElement extends SugarElement {
  static override properties = {
    ...SugarElement.properties,
    sampleLevel: { type: Number, attribute: "sample-level" },
    unbiased: { type: Boolean },
  };

  sampleLevel = 5;
  unbiased = false;

  createEntity(): Entity | undefined {
    return new SugarTAAPostProcessEntity();
  }

  /**
   * Lit calls `update()` before `firstUpdated()`, so attribute-driven changes
   * on the first render hit an undefined entity and are dropped. Push the
   * current property values into the entity right after it's created so TAA
   * starts with the user-provided `sample-level` / `unbiased`.
   */
  override firstUpdated(e: unknown): void {
    super.firstUpdated(e as never);
    const ent = this.entity as SugarTAAPostProcessEntity | undefined;
    if (!ent) return;
    ent.setSampleLevel(this.sampleLevel);
    ent.setUnbiased(this.unbiased);
  }

  protected override update(changedProperties: PropertyValues): void {
    super.update(changedProperties);
    const ent = this.entity as SugarTAAPostProcessEntity | undefined;
    if (!ent) return;
    if (changedProperties.has("sampleLevel")) {
      ent.setSampleLevel(this.sampleLevel);
    }
    if (changedProperties.has("unbiased")) {
      ent.setUnbiased(this.unbiased);
    }
  }
}
